import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Badge, Card, Skeleton } from '@/design-system/primitives'
import { formatMoney } from '@/lib/money'

import { useAdminQuery } from '../api'
import type { SellersReport } from '../types'

/**
 * Qui a vendu quoi, et pour combien.
 *
 * Le pendant exact du tableau des dépenses. La question se pose le lendemain
 * matin, une caisse posée sur la table : qui tenait quoi, et qu'est-ce qui est
 * passé par ses mains ? Sans réponse chiffrée, elle devient une discussion.
 *
 * Le regroupement se fait sur l'auteur de la saisie — le caissier qui
 * encaisse, le collaborateur qui note une commande en salle, celui qui tient
 * la porte. Les ventes en ligne n'ont pas d'opérateur : elles figurent à part
 * plutôt que d'être écartées, faute de quoi le total du tableau ne retomberait
 * pas sur le chiffre d'affaires — et un tableau qui ne retombe pas ne sert à
 * rien.
 *
 * La part encaissée **en espèces** est affichée à côté du total, parce que
 * c'est elle, et elle seule, qu'on va compter à la main.
 */
export function SellersPanel({ eventId }: { eventId: string | undefined }) {
  const { t, i18n } = useTranslation()
  const [open, setOpen] = useState<string | null>(null)

  const query = useAdminQuery<SellersReport>(
    ['admin-sellers', eventId],
    `/admin/events/${eventId}/forecast/sellers`,
    eventId,
  )

  if (query.isPending) return <Skeleton className="mt-8 h-40 w-full" />
  // Sans le droit de lecture, le panneau s'efface plutôt que d'afficher une
  // erreur : ce n'est pas une panne, c'est un tableau qui ne le concerne pas.
  if (query.isError) return null

  const report = query.data
  if (!report) return null

  const money = (minor: number) =>
    formatMoney({ amount_minor: minor, currency: report.currency }, i18n.language)

  return (
    <Card className="mt-8">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-baseline sm:justify-between">
        <div className="min-w-0">
          <h2 className="font-display text-heading-md text-content-primary">
            {t('admin.sellers.title')}
          </h2>
          <p className="mt-1 max-w-xl text-sm text-content-secondary">
            {t('admin.sellers.hint')}
          </p>
        </div>
        <p className="shrink-0 sm:text-right">
          <span className="block text-caption uppercase tracking-wide text-content-muted">
            {t('admin.sellers.total')}
          </span>
          <span className="font-display text-heading-md tabular-nums text-content-primary">
            {money(report.total_minor)}
          </span>
        </p>
      </div>

      {report.sellers.length === 0 ? (
        <p className="mt-5 text-sm text-content-secondary">{t('admin.sellers.empty')}</p>
      ) : (
        <ul className="mt-5 grid gap-2">
          {report.sellers.map((seller) => {
            const key = seller.user_id ?? seller.name
            const expanded = open === key
            return (
              <li key={key} className="rounded-lg border border-line-subtle">
                <button
                  type="button"
                  onClick={() => setOpen(expanded ? null : key)}
                  aria-expanded={expanded}
                  className="w-full px-4 py-3 text-left"
                >
                  <span className="flex items-center gap-3">
                    <span className="min-w-0 flex-1 truncate text-sm text-content-primary">
                      {seller.name}
                    </span>
                    <span className="shrink-0 font-display text-heading-sm tabular-nums text-content-primary">
                      {money(seller.revenue_minor)}
                    </span>
                    <span aria-hidden className="shrink-0 text-content-muted">
                      {expanded ? '−' : '+'}
                    </span>
                  </span>
                  <span className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span className="text-caption text-content-muted">
                      {t('admin.sellers.count', { count: seller.sales_count })}
                    </span>
                    {seller.cash_minor > 0 ? (
                      <Badge tone="champagne">
                        {t('admin.sellers.cash', { amount: money(seller.cash_minor) })}
                      </Badge>
                    ) : null}
                  </span>
                </button>

                {expanded ? (
                  <ul className="border-t border-line-subtle px-4 py-2">
                    {seller.lines.map((line) => (
                      <li
                        key={line.label}
                        className="flex items-baseline gap-3 py-1.5"
                      >
                        <span className="shrink-0 tabular-nums text-caption text-content-muted">
                          {line.quantity} ×
                        </span>
                        <span className="min-w-0 flex-1 truncate text-sm text-content-primary">
                          {line.label}
                        </span>
                        <span className="shrink-0 text-sm tabular-nums text-content-primary">
                          {money(line.revenue_minor)}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </li>
            )
          })}
        </ul>
      )}

      {/* Les ventes en ligne n'ont pas d'opérateur. Les taire ferait un
          tableau qui ne retombe pas sur le chiffre d'affaires, et l'on
          passerait la matinée à chercher l'écart. */}
      {report.unattributed_minor > 0 ? (
        <p className="mt-4 text-caption text-content-muted">
          {t('admin.sellers.unattributed', {
            amount: money(report.unattributed_minor),
          })}
        </p>
      ) : null}
    </Card>
  )
}
